import { useParams, Navigate, Link } from "react-router-dom";
import PageShell from "@/components/site/PageShell";
import SEO from "@/components/site/SEO";
import JsonLd, { buildBreadcrumbs } from "@/components/site/JsonLd";
import TrustBand from "@/components/site/TrustBand";
import { servicePages } from "@/data/servicePages";
import { CheckCircle2, Users, MapPin, MessageCircle } from "lucide-react";
import { whatsappHref } from "@/lib/format";

export default function ServicePage() {
  const { slug } = useParams();
  const svc = servicePages.find((s) => s.slug === slug);

  if (!svc) return <Navigate to="/" replace />;

  const url = `https://www.transporteenvanesybuses.com/servicios/${svc.slug}`;

  return (
    <PageShell testid={`page-servicio-${svc.slug}`}>
      <SEO title={svc.metaTitle} description={svc.metaDescription} />
      <JsonLd
        id="breadcrumb-jsonld"
        data={buildBreadcrumbs([
          { name: "Inicio", url: "https://www.transporteenvanesybuses.com/" },
          { name: "Servicios", url },
          { name: svc.heroHighlight, url },
        ])}
      />
      <JsonLd
        id="service-jsonld"
        data={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": `${svc.heroBefore} ${svc.heroHighlight} ${svc.heroAfter}`,
          "serviceType": svc.serviceType,
          "provider": { "@id": "https://www.transporteenvanesybuses.com/#organization" },
          "areaServed": { "@type": "City", "name": svc.areaServedName },
          "url": url,
          "description": svc.metaDescription,
        }}
      />

      <section className="relative pt-16 md:pt-24 pb-16 md:pb-20">
        <div className="orb-gold w-[500px] h-[500px] left-[-140px] top-[80px]" />
        <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
          <div className="text-[#E8C87A] text-xs uppercase tracking-[0.35em] mb-4">{svc.kicker}</div>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.05] text-[#F5EFE0] max-w-4xl">
            {svc.heroBefore} <span className="italic text-gold-grad">{svc.heroHighlight}</span> {svc.heroAfter}
          </h1>
          <p className="text-[#F5EFE0]/70 mt-5 max-w-2xl text-base md:text-lg leading-relaxed">
            {svc.intro}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/cotizar" className="btn-gold px-7 py-3 rounded-full text-sm">Cotizar ahora</Link>
            <a href={whatsappHref("573123616674", `Hola, quiero información sobre ${svc.heroHighlight}`)} target="_blank" rel="noreferrer" className="btn-ghost-gold px-7 py-3 rounded-full text-sm inline-flex items-center gap-2">
              <MessageCircle size={15} /> Escribir por WhatsApp
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 border-t border-[#3A3229]/60">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-10">
            <div className="text-[#E8C87A] text-xs uppercase tracking-[0.3em] mb-4">¿Para quién es?</div>
            <h2 className="font-display text-3xl md:text-4xl text-[#F5EFE0]">Este servicio está pensado para</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 max-w-4xl">
            {svc.paraQuien.map((p) => (
              <div key={p} className="flex items-start gap-2 text-sm text-[#F5EFE0]/80 p-4 rounded-xl border border-[#3A3229]">
                <CheckCircle2 size={16} className="text-[#E8C87A] mt-0.5 shrink-0" /> {p}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 border-t border-[#3A3229]/60">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-3 gap-5">
          <div className="card-lux p-6">
            <Users size={22} className="text-[#E8C87A] mb-3" />
            <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2">Capacidad</div>
            <p className="text-sm text-[#F5EFE0]/75 leading-relaxed">{svc.capacidad}</p>
          </div>
          <div className="card-lux p-6">
            <MapPin size={22} className="text-[#E8C87A] mb-3" />
            <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2">Dónde operamos</div>
            <p className="text-sm text-[#F5EFE0]/75 leading-relaxed">{svc.desdeDonde}</p>
          </div>
          <div className="card-lux p-6">
            <MessageCircle size={22} className="text-[#E8C87A] mb-3" />
            <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2">Cómo cotizar</div>
            <p className="text-sm text-[#F5EFE0]/75 leading-relaxed">{svc.comoCotizar}</p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 border-t border-[#3A3229]/60">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-10">
            <div className="text-[#E8C87A] text-xs uppercase tracking-[0.3em] mb-4">Qué incluye</div>
            <h2 className="font-display text-3xl md:text-4xl text-[#F5EFE0]">Todo coordinado, sin sorpresas</h2>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3 max-w-4xl">
            {svc.incluye.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-[#F5EFE0]/80">
                <CheckCircle2 size={16} className="text-[#E8C87A] mt-0.5 shrink-0" /> {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {svc.venues && (
        <section className="py-16 md:py-20 border-t border-[#3A3229]/60">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <div className="max-w-2xl mb-10">
              <div className="text-[#E8C87A] text-xs uppercase tracking-[0.3em] mb-4">Escenarios en Bogotá</div>
              <h2 className="font-display text-3xl md:text-4xl text-[#F5EFE0]">Te llevamos a los principales lugares de eventos</h2>
              <p className="text-[#F5EFE0]/60 mt-3 text-sm">Conocemos las rutas y accesos de estos escenarios para que llegues a tiempo.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {svc.venues.map((v) => (
                <div key={v.name} className="card-lux p-5 flex gap-4 items-start">
                  <div className="w-11 h-11 shrink-0 rounded-lg bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center font-display text-lg text-[#E8C87A]">
                    {v.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-display text-lg text-[#F5EFE0]">{v.name}</div>
                    <div className="text-xs text-[#F5EFE0]/55 mt-1 flex items-start gap-1">
                      <MapPin size={12} className="text-[#D4AF37] mt-0.5 shrink-0" /> {v.address}
                    </div>
                    <div className="text-xs text-[#F5EFE0]/50 mt-1">{v.note}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16 md:py-20 border-t border-[#3A3229]/60">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-10">
            <div className="text-[#E8C87A] text-xs uppercase tracking-[0.3em] mb-4">Vehículos disponibles</div>
            <h2 className="font-display text-3xl md:text-4xl text-[#F5EFE0]">Elige según tu grupo</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {svc.vehiculos.map((v) => (
              <div key={v.size} className="card-lux p-6">
                <div className="flex items-center gap-2 text-[#E8C87A] text-xs uppercase tracking-widest mb-3">
                  <Users size={13} /> {v.size}
                </div>
                <div className="font-display text-xl text-[#F5EFE0] mb-2">{v.vehicle}</div>
                <div className="text-sm text-[#F5EFE0]/65">{v.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TrustBand />
    </PageShell>
  );
}
